import { useRef } from 'react';
import { Route, useLocation } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import PropTypes from 'prop-types';

import DailyTasksView from '~/components/DailyTasksView';
import LootDetailView from '~/components/LootPackViews/LootDetailView';
import PurchaseView from '~/components/LootPackViews/PurchaseView';
import RecruitmentView from '~/components/RecruitmentView';
import SubscriptionOverview from '~/components/SubscriptionOverview';
import TutorialView from '~/components/TutorialView';

import EarnCoinsPopup from '~/popups/EarnCoinsPopup';
import EarnDiamondsPopup from '~/popups/EarnDiamondsPopup';
import { MenuPopup } from '~/popups/MenuPopup';
import { setPopupPath } from '~/redux/slices/location';
import {
  CARD_PATH,
  COLLECTION_PATH,
  DAILY_PATH,
  DEALS_PATH,
  EARN_COINS_PATH,
  EARN_DIAMONDS_PATH,
  EXPLORATION_QUEST_PATH,
  isPopupPath,
  MENU_PATH,
  seasonTicketModalPaths,
  SURVEY_PATHS,
  TUTORIAL_PATH_CREATOR,
} from '~/routes/paths';
import CardDetailView from '~/views/CardDetailView';
import CollectionView from '~/views/CollectionView';
import DealsView from '~/views/DealsView';
import { ExplorationQuestView } from '~/views/ExplorationQuestView';
import { RADAR_PRIVACY_PATH, RADAR_TERMS_PATH } from '~/views/RadarView/paths';
import RadarPrivacyPopup from '~/views/RadarView/popups/RadarPrivacyPopup';
import RadarTermsPopup from '~/views/RadarView/popups/RadarTermsPopup';
import { SurveyView } from '~/views/SurveyView';

import EnsureLoggedInRoute from './EnsureLoggedInRoute';
import { isMenuPopupPath } from './MenuRouter/menuRoutes';
import SwitchWithLocationTracker from './SwitchWithLocationTracker';

const PopupRouter = () => {
  const location = useLocation();
  const popupLocation = useRef(location);
  const { pathname } = location;

  // update `popupLocation` only if `location` don't matches a menu popout route
  if (!isMenuPopupPath(pathname)) {
    popupLocation.current = location;
  }
  const isPopupRoute = isPopupPath(popupLocation.current.pathname);

  return (
    <CSSTransition
      mountOnEnter
      unmountOnExit
      classNames="modal"
      timeout={300}
      in={isPopupRoute}
      appear
    >
      <SwitchWithLocationTracker
        location={popupLocation.current}
        setPath={setPopupPath}
        condition={isPopupRoute}
      >
        <EnsureLoggedInRoute
          component={SubscriptionOverview}
          path={seasonTicketModalPaths}
        />
        <Route
          component={MenuPopup}
          path={MENU_PATH}
        />
        <Route
          component={EarnCoinsPopup}
          path={EARN_COINS_PATH}
        />
        <Route
          component={EarnDiamondsPopup}
          path={EARN_DIAMONDS_PATH}
        />
        <Route
          component={RecruitmentView}
          path="/recruit"
        />
        <EnsureLoggedInRoute
          component={PurchaseView}
          path="/loots/:lootId/purchase"
        />
        <Route
          component={LootDetailView}
          path="/loots/:lootId"
        />
        <Route
          component={CardDetailView}
          path={CARD_PATH}
        />
        <Route
          component={TutorialView}
          path={TUTORIAL_PATH_CREATOR()}
        />
        <EnsureLoggedInRoute
          component={DailyTasksView}
          path={DAILY_PATH}
        />
        <Route
          component={RadarTermsPopup}
          path={RADAR_TERMS_PATH}
        />
        <Route
          component={RadarPrivacyPopup}
          path={RADAR_PRIVACY_PATH}
        />
        <Route
          component={CollectionView}
          path={COLLECTION_PATH}
        />
        <Route
          component={ExplorationQuestView}
          path={EXPLORATION_QUEST_PATH}
        />
        <Route
          component={SurveyView}
          path={SURVEY_PATHS}
        />
        <Route
          component={DealsView}
          path={DEALS_PATH}
        />
      </SwitchWithLocationTracker>
    </CSSTransition>
  );
};

PopupRouter.propTypes = {
  captchaRequired: PropTypes.bool,
};

export default PopupRouter;
